import React, { useEffect, useRef, useState } from "react";
import { useClient } from "../../context/ClientContext";
import LayoutStyles from "../../App.styles";
import QRCodeStyling from "qr-code-styling";
import adtLogo from "../../assets/images/adt-logo.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownToLine,
  faLinkSimple,
  faXmark,
} from "@fortawesome/pro-solid-svg-icons";
import AppleWallet from "../../assets/images/apple_wallet.svg";
import GooglePay from "../../assets/images/google_pay.svg";
import { createWalletPass } from "../../actions";
import { AnalyticsInputType } from "../../types/AnalyticsTypes";

type Props = {
  pfp: string;
  closeModal: React.Dispatch<React.SetStateAction<boolean>>;
  urlLink: string;
  name: string;
  location: string;
  title: string;
};

const QRModal: React.FC<Props> = ({
  pfp,
  name,
  location,
  title,
  closeModal,
  urlLink,
}) => {
  const { QrModal } = LayoutStyles;
  const ref = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  const [qrCode, setQrCode] = useState<QRCodeStyling | null>(null);

  const isAppleDevice = /Mac|iPhone|iPad|iPod/.test(navigator.userAgent);
  const { profileData, handleAppAnalytics } = useClient();
  const centerImage = pfp.length > 0 ? pfp : adtLogo;

  useEffect(() => {
    // Preload image with CORS to avoid CORS errors
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = centerImage;

    img.onload = () => {
      const qr = new QRCodeStyling({
        width: 232,
        height: 232,
        data: urlLink,
        image: centerImage,
        qrOptions: {
          typeNumber: 0,
          mode: "Byte",
          errorCorrectionLevel: "Q",
        },
        dotsOptions: {
          color: "black",
          type: "dots",
        },
        cornersDotOptions: {
          type: "dot",
        },
        cornersSquareOptions: {
          type: "extra-rounded",
        },
        imageOptions: {
          crossOrigin: "anonymous",
          margin: 10,
          imageSize: 0.4,
          hideBackgroundDots: true,
        },
      });
      setQrCode(qr);
    };

    img.onerror = () => {
      // If image fails to load with CORS, create QR without image
      const qr = new QRCodeStyling({
        width: 232,
        height: 232,
        data: urlLink,
        qrOptions: {
          typeNumber: 0,
          mode: "Byte",
          errorCorrectionLevel: "Q",
        },
        dotsOptions: {
          color: "black",
          type: "dots",
        },
        cornersDotOptions: {
          type: "dot",
        },
        cornersSquareOptions: {
          type: "extra-rounded",
        },
      });
      setQrCode(qr);
    };
  }, [centerImage, urlLink]);

  useEffect(() => {
    if (qrCode && ref.current) {
      const canvasContainer = document.getElementById("qr-container");
      const canvasCreated =
        ref.current && ref.current?.getElementsByTagName("canvas").length > 0;
      console.log("qrCode", qrCode);
      if (
        !canvasCreated &&
        canvasContainer &&
        canvasContainer.getElementsByTagName("canvas").length === 0
      ) {
        qrCode.append(canvasContainer);
      }
    }
  }, [qrCode]);

  const handleWalletSaveClick = async (walletType: "ios" | "android") => {
    const walletPacket = {
      name: profileData.name,
      job_title: profileData.job_title,
      location: profileData.location,
      phone_number: profileData.phone,
      email: profileData.email,
      qrcode: window.location.href,
      type: walletType,
      publicID: profileData.publicID,
    };
    console.log(walletPacket);
    createWalletPass(walletPacket)
      .then(async (res: Blob | { result: string; url: string }) => {
        if (walletType === "ios") {
          await triggerCommunicationAnalytics(walletType + "-wallet-save");
          const url = window.URL.createObjectURL(res as Blob);
          const downloadLink = document.createElement("a");
          downloadLink.href = url;
          downloadLink.download = "contactcard.pkpass";

          // Append the anchor tag and trigger the download
          document.body.appendChild(downloadLink);
          downloadLink.click();

          // Cleanup the DOM
          document.body.removeChild(downloadLink);
          console.log("wallet res", res);
        } else if (typeof res === "object") {
          let obj = res as { result: string; url: string };
          if (obj.result === "SUCCESS") {
            await triggerCommunicationAnalytics(walletType + "-wallet-save");
            window.open(obj.url as any, "_blank");
          }
        }
      })
      .catch((err: any) => {
        console.log("wallet error", err);
      });
  };

  const triggerCommunicationAnalytics = async (event_action: string) => {
    // eventAction?.preventDefault();
    const event: AnalyticsInputType = {
      hotspot_id: profileData.publicID,
      content_type_id: "",
      event_category: "virtual-wallet-save",
      event_action: event_action,
      label: "smart-profile",
      string_value: "",
      numeric_value: 0,
      completion_value: 0,
      duration: 0,
      spend_value: 0,
      name: profileData.email,
    };
    console.log(event);
    await handleAppAnalytics(event);
  };

  const handleCopyclick = () => {
    let copyTimer;
    clearTimeout(copyTimer);
    navigator.clipboard.writeText(urlLink);
    setCopied(true);

    copyTimer = setTimeout(() => {
      setCopied(false);
    }, 3000);
  };

  const createStyledQRDownload = (isSharing: boolean = false) => {
    console.log("createStyledQRDownload called, isSharing:", isSharing);
    const originalCanvas = document.getElementsByTagName("canvas")[0];
    if (!originalCanvas) {
      console.log("Canvas not found, using QR library download");
      if (qrCode) {
        qrCode.download({ extension: "png" });
      }
      return;
    }

    console.log("Canvas found, original dimensions:", originalCanvas.width, originalCanvas.height);
    const newImage = new Image();
    newImage.src = centerImage;
    newImage.crossOrigin = "anonymous";
    console.log("Loading image:", centerImage);

    newImage.onload = () => {
      console.log("Image loaded successfully, dimensions:", newImage.width, newImage.height);
      const destinationCanvas = document.createElement("canvas");
      destinationCanvas.width = originalCanvas.width;
      destinationCanvas.height = originalCanvas.height;
      const destCtx = destinationCanvas.getContext("2d");
      if (!destCtx) return;

      // Draw the original QR code
      destCtx.drawImage(originalCanvas, 0, 0);

      // Create circular mask for the center image to match the QR library settings exactly
      // QR library uses: imageSize: 0.4, margin: 10
      const qrSize = Math.min(destinationCanvas.width, destinationCanvas.height);
      const imageAreaSize = qrSize * 0.4; // Match imageSize: 0.4 from QR library
      const margin = 10; // Match margin: 10 from QR library
      const containerSize = imageAreaSize - (margin * 2); // Account for margin on both sides
      const containerX = (destinationCanvas.width - containerSize) / 2;
      const containerY = (destinationCanvas.height - containerSize) / 2;
      const radius = containerSize / 2;

      // Calculate cover scaling (like CSS background-size: cover)
      const imgWidth = newImage.naturalWidth || newImage.width;
      const imgHeight = newImage.naturalHeight || newImage.height;

      // Calculate scale to cover the container (like CSS background-size: cover)
      const scaleX = containerSize / imgWidth;
      const scaleY = containerSize / imgHeight;
      const scale = Math.max(scaleX, scaleY); // Use larger scale to ensure full coverage

      // Calculate final dimensions
      const drawWidth = imgWidth * scale;
      const drawHeight = imgHeight * scale;

      // Center the scaled image
      const offsetX = containerX + (containerSize - drawWidth) / 2;
      const offsetY = containerY + (containerSize - drawHeight) / 2;

      console.log("QR and image dimensions:", {
        qrSize,
        imageAreaSize,
        containerSize,
        margin,
        containerX,
        containerY
      });

      console.log("Cover scaling:", {
        imgWidth,
        imgHeight,
        scaleX,
        scaleY,
        scale,
        drawWidth,
        drawHeight,
        offsetX,
        offsetY
      });

      // Save the current context state
      destCtx.save();

      // Create circular clipping path
      destCtx.beginPath();
      destCtx.arc(containerX + radius, containerY + radius, radius, 0, Math.PI * 2);
      destCtx.clip();

      // Draw the image with cover scaling within the circular clip
      destCtx.drawImage(newImage, offsetX, offsetY, drawWidth, drawHeight);

      // Restore the context state
      destCtx.restore();

      // Convert to blob and handle accordingly
      destinationCanvas.toBlob((blob) => {
        if (!blob) return;

        if (isSharing) {
          const file = new File([blob], "qr-code.png", { type: "image/png" });
          navigator.share({
            title: "QR Code - " + name,
            text: "Check out this QR code",
            files: [file],
          }).catch((error) => {
            console.log("Error sharing:", error);
            // Fallback to download on share error
            downloadBlob(blob, "qr-code.png");
          });
        } else {
          // Direct download
          downloadBlob(blob, "qr-code.png");
        }
      }, "image/png");
    };

    newImage.onerror = () => {
      console.log("Image load failed, using QR library download");
      if (qrCode) {
        qrCode.download({ extension: "png" });
      }
    };
  };

  const onDownloadClick = async () => {
    console.log("=== DOWNLOAD BUTTON CLICKED ===");
    console.log("Download clicked - using styled QR function");

    // Force our custom download always for testing
    console.log("FORCING CUSTOM DOWNLOAD FOR TESTING");
    createStyledQRDownload(false);
    return;

    // Check if we're on mobile and sharing is supported
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const hasWebShare = 'share' in navigator && 'canShare' in navigator;
    const canShare = isMobile && hasWebShare && navigator.canShare({ files: [] });

    console.log("Device detection:", { isMobile, hasWebShare, canShare });

    if (canShare) {
      // Mobile sharing with styled QR
      console.log("Using mobile sharing path");
      createStyledQRDownload(true);
    } else {
      // Desktop/non-mobile - download with styled QR
      console.log("Using desktop download path");
      createStyledQRDownload(false);
    }
  };

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const downloadLink = document.createElement("a");
    downloadLink.href = url;
    downloadLink.download = filename;
    downloadLink.style.display = "none";

    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);

    // Clean up the URL object
    window.URL.revokeObjectURL(url);
  };

  return (
    <QrModal.Backdrop>
      {/* This field is hidden and gets assigned the qr code as a file in order to dynamically save and send it
        to wallet */}
      <input
        type="file"
        id="fileField"
        name="myfile"
        style={{ display: "none" }}
      ></input>
      <QrModal.Box>
        <QrModal.CloseButton onClick={() => closeModal(false)}>
          <FontAwesomeIcon size="2x" color="#fff" icon={faXmark} />
        </QrModal.CloseButton>
        <QrModal.QRContainer>
          <div id="qr-container" ref={ref} style={{ position: "relative" }}>
            <QrModal.ImageContainer pfp={centerImage} />
          </div>
          <QrModal.Name>{name}</QrModal.Name>
          <QrModal.Title>{title}</QrModal.Title>
          <QrModal.Location>{location}</QrModal.Location>
        </QrModal.QRContainer>
        <QrModal.QRButton onClick={handleCopyclick}>
          {!copied && (
            <FontAwesomeIcon
              style={{ marginRight: "12px" }}
              color="#474D5D"
              icon={faLinkSimple}
            />
          )}
          {copied ? "Copied!" : "Copy link"}
        </QrModal.QRButton>
        <QrModal.QRButton onClick={onDownloadClick}>
          <FontAwesomeIcon
            style={{ marginRight: "12px" }}
            color="#474D5D"
            icon={faDownToLine}
          />
          Save as image
        </QrModal.QRButton>
        {isAppleDevice ? (
          <img
            onClick={() => handleWalletSaveClick("ios")}
            style={{ height: "64px", width: "100%", marginTop: "15px" }}
            src={AppleWallet}
            alt="Add to Apple Wallet"
          />
        ) : (
          <img
            onClick={() => handleWalletSaveClick("android")}
            src={GooglePay}
            style={{ width: "84%", margin: "11px auto 0" }}
            alt="Add to Google Wallet"
          />
        )}
      </QrModal.Box>
    </QrModal.Backdrop>
  );
};

export default QRModal;
